//-- angular core
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable, tap, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';


//-- primefaces messages
import {MessageService} from 'primeng/api';

//-- app
import {BaseService} from './base/base.service';
import {ResponseXBVO} from './base/base.model';
import {
  BsaIncomingMessagesXBVO,
  BsaInvoiceLinksXBVO,
  BsaMenuDetailsXBVO,
  BsaMenuPlanLinksXBVO,
  BsaOrderHistoryXBVO,
  BsaPrepaidAutoChargeXBVO,
  BsaProfileInfoXBVO,
  IncomingMessageXBVO,
  SaldoChangeXBVO,
  PasswordChangeXBVO,
  SaldoDetailsXBVO
} from './model/xbvo.model';
import {JwtHelperService} from "@auth0/angular-jwt";
import {DateUtilsBs} from "./base/utils/dateUtilsBs";
import {AuthService} from "./base/auth/auth.service";
import {BusyService} from "./base/busy/busy.service";
import {environment} from "../../environments/environment";
import {
  BsaOrderPlaceholdersXBVO,
  BsaOrderPlaceholderXBVO,
  EnumMenuLineType,
  HomeNotificationModel
} from './model/dto.models.imported';
import {OneTimeNotificationDto} from "./model/dto.model";


@Injectable()
export class RestClient extends BaseService {

  static HTTP_FORM_URLENCODED = new HttpHeaders().append('Content-Type', `application/x-www-form-urlencoded`);

  /**
   * Constructor.
   */
  constructor(private httpClient: HttpClient,
              private jwtHelper: JwtHelperService,
              private authService: AuthService,
              private messageService: MessageService,
              private busyServce: BusyService) {
    super();
  }

  /**
   * Calls REST.
   */
  public orderPlaceholders(aYear: Number, aWeek: Number): Observable<BsaOrderPlaceholdersXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/order_placeholders/" + aYear + "/" + aWeek + "/1";
    return this.httpClient
      .get<BsaOrderPlaceholdersXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse=>console.log(aResponse)),
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  public getSaldoDetails(): Observable<SaldoDetailsXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/saldoDetails/";
    return this.httpClient
      .get<SaldoDetailsXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse=>console.log(aResponse)),
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public orderHistory(aYear: Number, aWeek: Number): Observable<BsaOrderHistoryXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/orderhistory/" + aYear + "/" + aWeek + "/10";
    return this.httpClient
      .get<BsaOrderHistoryXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }


  /**
   * Calls REST.
   */
  public messages(): Observable<BsaIncomingMessagesXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/messages";
    return this.httpClient
      .get<BsaIncomingMessagesXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Fetches the authoritative unread-message count without showing the global busy indicator.
   */
  public unreadMessageCount(): Observable<number> {
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/messages/unread_count";
    return this.httpClient.get<number>(pUrl);
  }

  /**
   * Calls REST.
   */
  public messageReaded(aMessageDeliveryId: number): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/message_readed/" + aMessageDeliveryId;
    return this.httpClient
      .get<ResponseXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }


  /**
   * Calls REST.
   */
  public messageDelete(aMessageId: number): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/message_delete/" + aMessageId;
    return this.httpClient
      .get<ResponseXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public messageSend(aMessageXBVO: IncomingMessageXBVO): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/message_send";
    return this.httpClient
      .post<ResponseXBVO>(pUrl, aMessageXBVO, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public invoiceLinks(): Observable<BsaInvoiceLinksXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/invoicelinks";
    return this.httpClient
      .get<BsaInvoiceLinksXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }


  /**
   * Calls REST.
   */
  public invoicePDF(aInvoiceNr: string): Observable<Blob> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/invoicepdf/" + aInvoiceNr;
    return this.httpClient
      .get(pUrl, {responseType: 'blob', observe: 'response', headers: {}})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => {
          return new Blob([aResponse.body], {type: 'application/pdf'});
        })
      );
  }

  /**
   * Calls REST.
   */
  public menuPlanLinks(aYear: Number, aWeek: Number): Observable<BsaMenuPlanLinksXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/menuplanlinks/" + aYear + "/" + aWeek + "/8";
    return this.httpClient
      .get<BsaMenuPlanLinksXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public menuplanPDF(aYear: Number, aWeek: Number): Observable<Blob> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/menuplanpdf/" + aYear + "/" + aWeek;
    return this.httpClient
      .get(pUrl, {responseType: 'blob', observe: 'response', headers: {}})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => {
          return new Blob([aResponse.body], {type: 'application/pdf'});
        })
      );
  }

  /**
   * Calls REST.
   */
  public menuDetails(aServiceDay: Date, aMenuLine: EnumMenuLineType): Observable<BsaMenuDetailsXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/menu_details/" + DateUtilsBs.formatYYYYMMDD(aServiceDay) + "/" + aMenuLine;
    return this.httpClient
      .get<BsaMenuDetailsXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public profileInfo(): Observable<BsaProfileInfoXBVO> {
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/profile";
    return this.asPipeBusy(this.httpClient.get<BsaProfileInfoXBVO>(pUrl, {observe: 'response'}))
  }

  /**
   * Calls REST.
   */
  public profileNotifyPlanChanged(aNotificationActive: boolean): Observable<any> {
    return this.asPipeBusy(
      this.httpClient.post(this.getURL('/profile_notify_plan_changed'), aNotificationActive, {observe: 'response'}));
  }

  /**
   * Calls REST.
   */
  public saveProfile(aProfileXBVO: BsaProfileInfoXBVO): Observable<BsaProfileInfoXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/save_profile";
    return this.httpClient
      .post<BsaProfileInfoXBVO>(pUrl, aProfileXBVO, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public changePassword(passwordChangeXBVO: PasswordChangeXBVO): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/change_password";
    return this.httpClient
      .post(pUrl, passwordChangeXBVO, {observe: 'response', headers: AuthService.HTTP_FORM_URLENCODED})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public prepaidStatus(): Observable<BsaPrepaidAutoChargeXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/prepaid/status";
    return this.httpClient
      .get<BsaPrepaidAutoChargeXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public prepaidSetAutoChargeAmount(aAmount: number): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/prepaid/set_autocharge_amount";
    return this.httpClient
      .post<ResponseXBVO>(pUrl, aAmount, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   */
  public prepaidChargeOnce(aAmount: number): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/prepaid/charge_once";
    return this.httpClient
      .post<ResponseXBVO>(pUrl, aAmount, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   * Date format: yyyy-MM-dd'T'HH:mm:ss
   */
  public changeOrderCount(order: BsaOrderPlaceholderXBVO): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/change_order_count/";
    return this.httpClient
      .post<ResponseXBVO>(pUrl, order, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   * Date format: yyyy-MM-dd'T'HH:mm:ss
   */
  public placeOrder(aServiceDay: Date, aMenuLine: EnumMenuLineType): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/place_order/" + DateUtilsBs.formatYYYYMMDD(aServiceDay) + "/" + aMenuLine.toString();
    return this.httpClient
      .get<ResponseXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * Calls REST.
   * Date format: yyyy-MM-dd'T'HH:mm:ss
   */
  public cancelOrder(aServiceDay: Date, aMenuLine: EnumMenuLineType): Observable<ResponseXBVO> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/cancel_order/" + DateUtilsBs.formatYYYYMMDD(aServiceDay) + "/" + aMenuLine.toString();
    return this.httpClient
      .get<ResponseXBVO>(pUrl, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  public callValidateIBAN(aIBAN: String) {
    const pUrl = this.authService.resolve_REST_URL_By_Token() + "/validate_iban/" + aIBAN;
    return this.httpClient.get<ResponseXBVO>(pUrl, {observe: 'response'});
  }

  public callValidateBIC(aBIC: String) {
    const pUrl = this.authService.resolve_REST_URL_By_Token() + "/validate_bic/" + aBIC;
    return this.httpClient.get<ResponseXBVO>(pUrl, {observe: 'response'});
  }


  /**
   * Calls REST
   */
  public downloadContractPDF(): Observable<HttpResponse<Blob>> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/download_contract_pdf";
    return this.httpClient
      .get(pUrl, {observe: 'response', responseType: 'blob'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
      );
  }

  /**
   * Calls REST
   */
  public downloadSepaPDF(): Observable<HttpResponse<Blob>> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/download_sepa_pdf";
    return this.httpClient
      .get(pUrl, {observe: 'response', responseType: 'blob'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
      );
  }

  /**
   * Calls REST.
   */
  public getJsonResource<T>(aJsonPath: string) {
    this.markBusy();
    this.httpClient.get<string>(aJsonPath, {observe: 'response'})
      .pipe(catchError((aErrorResponse: HttpErrorResponse) => {
        return this.onError(aErrorResponse);
      }))
      .subscribe((aResponse: HttpResponse<T>) => {
        this.onSuccess(aResponse, aJsonPath);
      });
  }

  public getNotifications(): Observable<HomeNotificationModel[]> {
    return this.asPipeBusy(
      this.httpClient.get<HomeNotificationModel[]>(this.getURL('/notifications'), {observe: 'response'}));
  }

  public getNotificationsPersonalised(): Observable<OneTimeNotificationDto[]> {
    return this.asPipeBusy(
      this.httpClient.get<HomeNotificationModel[]>(this.getURL('/notifications_personalised'), {observe: 'response'}));
  }

  public setUserConfirmation(confirmationId: number, confirm: boolean): Observable<any> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/user_confirmation/" + confirmationId;
    return this.httpClient
      .post(pUrl, {}, {params: {confirm}, observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
      );
  }

  public setTerminationRequest(clientRequest: boolean, requestedLastDeliveryDate: Date, requestedRefundIBAN: String): Observable<any> {
    this.markBusy();
    let pUrl = this.authService.resolve_REST_URL_By_Token() + "/contract_termination";
    return this.httpClient
      .post(pUrl, {clientRequest, requestedLastDeliveryDate, requestedRefundIBAN}, {observe: 'response'})
      .pipe(
        tap(aResponse => this.onSuccess(aResponse, pUrl)),
        catchError(aErrorResponse => this.onError(aErrorResponse)),
        map(aResponse => aResponse.body)
      );
  }

  /**
   * REST-handler
   */
  public onSuccess(aResponse: HttpResponse<any>, aEndpoint?: string) {
    aEndpoint = aEndpoint != null ? aEndpoint : aResponse.url;

    // dismiss busy-spinner
    this.busyServce.markNotBusy();

    if (!environment.production) {
      var pLog = "[HTTP-ENDPOINT] " + aEndpoint + "]\n";
      pLog += "\t[HTTP-STATUS] " + aResponse.status + "/" + aResponse.statusText + "\n";
      pLog += "\t[HTTP-HEADER]\n";
      aResponse.headers.keys().forEach((aKey) => pLog += "\t\t\t" + aKey + ": " + aResponse.headers.get(aKey) + "\n");
      pLog += "\t[HTTP-BODY] " + JSON.stringify(aResponse.body) + "\n";
      this.logd(pLog);
    }
  }

  /**
   * Error handler
   */
  public onError(aErrorResponse: HttpErrorResponse): Observable<any> {
    this.logi("[RestService.onError] " + JSON.stringify(aErrorResponse));
    let pMaintenanceCODES = [404, 503, 504];

    // hide busy "spinner"
    this.busyServce.markNotBusy();


    // -------------------------------
    // HTTP/422 - Business violations
    // -------------------------------
    if (aErrorResponse.status == 422) {
      var pResponseXBVO: ResponseXBVO = aErrorResponse.error;
      pResponseXBVO.httpStatus = aErrorResponse.status;
      this.messageService.clear();
      let pUserMessage = pResponseXBVO.errorDescription;

      // #1 handle password change!
      if (pResponseXBVO.error == 'E_INVALID_PASSWORD') {
        this.authService.logout();
      }

      // #2 handle expored refresh-token!
      if (pResponseXBVO.error == 'E_REFRESH_TOKEN_INVALID') {
        pUserMessage = this.i18n.v('refreshTokenExpired');
      }

      // show user message
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: pUserMessage,
        key: "toast-messages",
        life: 10000,
        closable: true
      });
      return throwError("[Fehler] " + pResponseXBVO.errorDescription);

      // -------------------------------
      // Server Maintenance
      // HTTP/404 - Not Found
      // HTTP/503 - Service Unavailable
      // HTTP/504 - Gateway Timeout
      // -------------------------------
    } else if (pMaintenanceCODES.includes(aErrorResponse.status)) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        life: 10000,
        closable: true,
        detail: 'Diese App ist wegen Wartungsarbeiten zur Zeit nicht erreichbar. Wir sind gleich zurück... [' + this.parseHostname(aErrorResponse.url) + '|' + aErrorResponse.statusText + ']',
        key: "toast-messages"
      });
      return throwError("[Fehler] " + aErrorResponse.error);

      // -------------------------------
      // other errors
      // -------------------------------
    } else {
      console.log('publishing new Message... ');
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: aErrorResponse.error + " " + aErrorResponse.message,
        key: "toast-messages",
        life: 10000,
        closable: true
      });
      return throwError("[" + aErrorResponse.status + "] " + aErrorResponse.error);
    }
  }

  private parseHostname(aFullURL?: string) {
    if (aFullURL == null) {
      return aFullURL;
    }
    try {
      let pProtocol = aFullURL.substring(0, aFullURL.indexOf("://") + 3);
      let pWithoutProtocol = aFullURL.substring(pProtocol.length);
      return aFullURL.substring(0, pWithoutProtocol.indexOf("/") + pProtocol.length);
    } catch (e) {
      return aFullURL;
    }
  }

  /**
   * Marks app-busy.
   * @private
   */
  private markBusy() {
    // show busy-spinner
    this.busyServce.markBusy();
  }


  /**
   * Wrapps HttpResponse.
   * @param aResponse
   * @protected
   */
  protected asPipeBusy(aResponse: Observable<HttpResponse<any>>) {
    this.markBusy();
    return this.asPipe(aResponse);
  }

  /**
   * Wrapps HttpResponse.
   * @param aResponse
   * @protected
   */
  public asPipe(aResponse: Observable<HttpResponse<any>>) {
    return aResponse.pipe(
      tap(aResponse => this.onSuccess(aResponse)),
      catchError(aErrorResponse => this.onError(aErrorResponse)),
      map(aResponse => aResponse.body));
  }

  getURL(aServicePath: string) {
    return this.authService.resolve_REST_URL_By_Token() + aServicePath;
  }

}
